import { Dialog, Transition } from '@headlessui/react';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import Image from 'next/image';
import { Fragment, useMemo } from 'react';
import { Button } from './Button';
import PremiumIcon from './PremiumIcon';


type AlertModalProps = {
  bodyText?: string | React.ReactNode
  cancelButtonText?: string;
  confirmButtonText?: string;
  confirmButtonComponent?: React.ReactNode;
  isOpen: boolean;
  hasDivider?: boolean;
  isSingleButton?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  titleText: string;
  variant?: 'info' | 'error';
  hideCloseIcon?: boolean;
  directToTopup?: boolean;
};

export default function AlertModal(props: AlertModalProps) {
  const {
    bodyText,
    confirmButtonComponent,
    isOpen,
    onCancel,
    onConfirm,
    titleText,
    cancelButtonText = 'Cancel',
    confirmButtonText = 'OK',
    hasDivider,
    isSingleButton,
    variant = 'info',
    hideCloseIcon,
    directToTopup = false
  } = props;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOutsideClick = () => { };

  const renderIcon = useMemo(() => {
    switch (variant) {
      case 'info':
        return (
          <Image
            className="h-7 w-7"
            src="/images/info.svg"
            alt="info image"
            width={7}
            height={7}
            style={{ width: '100%' }}
          />
        );
      case 'error':
        return (
          <ExclamationTriangleIcon
            className="h-7 w-7 text-red-600"
            aria-hidden="true"
          />
        );
      default:
        return (
          <Image
            className="h-7 w-7"
            src="/images/info.svg"
            alt="info image"
            width={7}
            height={7}
            style={{ width: '100%' }}
          />
        );
    }
  }, [variant]);

  const getConfirmButtonClasses = (directToTopup, isSingleButton) => {
    return clsx(
      directToTopup 
        ? 'inline-block rounded border border-[#FFE2B4] bg-[#FFCA63] px-8 py-3 text-sm font-medium text-white hover:bg-[#FFCA63]/80 focus:outline-none active:bg-[#FFCA63] disabled:cursor-not-allowed disabled:bg-[#FFCA63] disabled:text-white disabled:opacity-60 disabled:hover:border-[#FFE2B4]' // Direct to topup classes
        : 'inline-block w-[90%] rounded-lg bg-blue-600 py-3 font-bold text-white hover:bg-blue-500 focus:outline-none focus:ring-blue-500 active:bg-blue-400 disabled:cursor-not-allowed disabled:bg-blue-600 disabled:text-white disabled:opacity-60 disabled:hover:border-blue-400 sm:w-1/5', // Default classes
      isSingleButton && 'mx-auto justify-center sm:mr-4',
    );
  }
  
  const confirmButtonClasses = useMemo(() => getConfirmButtonClasses(directToTopup, isSingleButton), [isSingleButton, directToTopup]);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50"
        onClose={handleOutsideClick}
      >
        <div className="fixed inset-0 z-20 overflow-y-auto">
          <div className="font-inter flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="elative m-auto transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <div className="mb-2 bg-white p-6 sm:pb-4">
                  <div className="sm:flex">
                    <div
                      className={clsx(
                        'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-12 sm:w-12',
                        variant === 'info' ? 'bg-blue-50' : 'bg-red-100',
                      )}
                    >
                      {renderIcon}
                    </div>
                    <div className="mt-3 px-4 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="relative flex justify-center text-2xl font-bold leading-tight text-gray-900 sm:justify-start"
                      >
                        {titleText}
                      </Dialog.Title>
                      <div className="my-2 text-xl font-normal leading-normal text-[#212121]/70">
                        {bodyText}
                      </div>
                    </div>
                    <div className="ml-auto hidden sm:flex">
                      {!hideCloseIcon && (
                        <XMarkIcon
                          className="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-300"
                          aria-hidden="true"
                          onClick={onCancel}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {hasDivider && <hr />}

                <div className="flex flex-col-reverse items-center justify-end gap-3 bg-[#F3F6FD] py-2 sm:flex-row sm:pr-5">
                  {!isSingleButton && (
                    <Button
                      type="button"
                      className="inline-block w-[90%] justify-center rounded-lg bg-[#F3F6FD] py-3 font-bold text-[#212121B2] ring-inset ring-gray-200 hover:bg-gray-50 sm:w-1/6 sm:shadow-sm sm:ring-1"
                      onClick={onCancel}
                    >
                      <span className="font-inter flex items-center justify-center text-sm">
                        {cancelButtonText}
                      </span>
                    </Button>
                  )}

                  {confirmButtonComponent ? (
                    <div
                      className={clsx(
                        isSingleButton && 'mx-auto justify-center sm:mr-4',
                      )}
                    >
                      {confirmButtonComponent}
                    </div>
                  ) : (
                    <Button
                      type="button"
                      className={confirmButtonClasses}
                      onClick={onConfirm}
                    >
                      <span className="font-inter flex items-center justify-center text-sm">
                        {confirmButtonText}
                        {directToTopup && 
                        <PremiumIcon className="ml-2 inline h-5 fill-white" />
                        }
                      </span>
                    </Button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
